import { CamelRequestError, retryAfterSecondsOf } from '@onestep/camel-api';
import type { CamelClient } from '@onestep/camel-api/hooks';
import axios, { isAxiosError } from 'axios';

import { apiGet, type ApiParams, camelUrl } from '@/config/apiRequest';

const KEEPALIVE_REQUEST_CONFIG = { adapter: 'fetch', fetchOptions: { keepalive: true } };

const retryAfterHeaderOf = (headers: unknown): string | undefined => {
  const value = (headers as Record<string, unknown> | undefined)?.['retry-after'];
  return typeof value === 'string' ? value : undefined;
};

const asCamelRequestError = (error: unknown, path: string): unknown =>
  isAxiosError(error) && error.response
    ? new CamelRequestError(
        error.response.status,
        path,
        error.response.data,
        retryAfterSecondsOf(retryAfterHeaderOf(error.response.headers)),
      )
    : error;

const rethrowAsCamelError = async <TResult>(
  path: string,
  request: () => Promise<TResult>,
): Promise<TResult> => {
  try {
    return await request();
  } catch (error) {
    throw asCamelRequestError(error, path);
  }
};

export const camelClient: CamelClient = {
  get: (path, params, signal) =>
    rethrowAsCamelError(path, () => apiGet(camelUrl(path), params as ApiParams, undefined, signal)),
  post: (path, body, signal, options) =>
    rethrowAsCamelError(
      path,
      async () =>
        (
          await axios.post(camelUrl(path), body, {
            signal,
            ...(options?.keepalive ? KEEPALIVE_REQUEST_CONFIG : {}),
          })
        ).data,
    ),
  put: (path, body, signal) =>
    rethrowAsCamelError(path, async () => (await axios.put(camelUrl(path), body, { signal })).data),
  patch: (path, body, signal) =>
    rethrowAsCamelError(
      path,
      async () => (await axios.patch(camelUrl(path), body, { signal })).data,
    ),
  delete: (path, options, signal) =>
    rethrowAsCamelError(
      path,
      async () =>
        (
          await axios.delete(camelUrl(path), {
            params: options?.params,
            data: options?.body,
            signal,
          })
        ).data,
    ),
};
